import { useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, ComposedChart } from "recharts";
import { TrendingUp, Users, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Analytics({ user, onLogout }) {
  const [kwhEvolution, setKwhEvolution] = useState([]);
  const [kwhByDistributor, setKwhByDistributor] = useState({ data: [], distributors: [] });
  const [invoicesByMonth, setInvoicesByMonth] = useState([]);
  const [invoicesCountByMonth, setInvoicesCountByMonth] = useState([]);
  const [cooperativesByMonth, setCooperativesByMonth] = useState([]);
  const [invoicesByStatusMonth, setInvoicesByStatusMonth] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'operador') {
      fetchAnalytics();
    }
  }, [user]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      const [evolution, distributor, invoices, invoicesCount, coopsByMonth, invoicesByStatus] = await Promise.all([
        axiosInstance.get(`/analytics/kwh-evolution`),
        axiosInstance.get(`/analytics/kwh-by-distributor`),
        axiosInstance.get(`/analytics/invoices-by-month`),
        axiosInstance.get(`/analytics/invoices-count-by-month`),
        axiosInstance.get(`/analytics/cooperatives-by-month`),
        axiosInstance.get(`/analytics/invoices-by-status-month`)
      ]);

      setKwhEvolution(evolution.data);
      setKwhByDistributor(distributor.data);
      setInvoicesByMonth(invoices.data);
      setInvoicesCountByMonth(invoicesCount.data);
      setCooperativesByMonth(coopsByMonth.data);
      setInvoicesByStatusMonth(invoicesByStatus.data);
    } catch (error) {
      toast.error("Erro ao carregar dados de analytics");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Format month for display (YYYY-MM -> MMM/YYYY)
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${months[parseInt(month) - 1]}/${year}`;
  };

  // Colors for distributors
  const distributorColors = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'
  ];

  if (user?.role !== 'admin') {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12">
          <p className="text-gray-600">Acesso restrito a administradores.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12">
          <p className="text-gray-600">Carregando gráficos...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-4xl font-bold text-gray-800">Gráficos e Analytics</h1>
            <p className="text-gray-600">Visualização de dados e tendências</p>
          </div>
        </div>

        {/* Date Filters removed - charts always show full historic data */}

        {/* Graph 1: kWh Evolution */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-gray-800">
              1. Evolução de kWh Contratado Total por Mês de Assinatura
            </CardTitle>
          </CardHeader>
          <CardContent>
            {kwhEvolution.length === 0 ? (
              <p className="text-center py-8 text-gray-500">Sem dados disponíveis</p>
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={kwhEvolution} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    tickFormatter={formatMonth}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    label={{ value: 'kWh', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => value.toLocaleString('pt-BR')}
                  />
                  <Tooltip 
                    formatter={(value, name) => [value.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) + ' kWh', name]}
                    labelFormatter={formatMonth}
                  />
                  <Legend />
                  <Bar dataKey="kwh" fill="#3b82f6" name="kWh Contratado (assinatura)" />
                  <Bar dataKey="cancelled_kwh" fill="#ef4444" name="Cancelados (Data Status)" />
                  <Bar dataKey="excluded_kwh" fill="#6b7280" name="Excluídos (Data Status)" />
                  <Bar dataKey="suspended_kwh" fill="#f97316" name="Suspensos (Data Status)" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Graph 2: kWh by Distributor */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-gray-800">
              2. kWh Contratado por Distribuidora e Mês de Assinatura
            </CardTitle>
          </CardHeader>
          <CardContent>
            {kwhByDistributor.data.length === 0 ? (
              <p className="text-center py-8 text-gray-500">Sem dados disponíveis</p>
            ) : (
              <ResponsiveContainer width="100%" height={500}>
                <BarChart data={kwhByDistributor.data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    tickFormatter={formatMonth}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    label={{ value: 'kWh', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => value.toLocaleString('pt-BR')}
                  />
                  <Tooltip 
                    formatter={(value) => [value.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) + ' kWh', '']}
                    labelFormatter={formatMonth}
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  {kwhByDistributor.distributors.map((dist, index) => (
                    <Bar 
                      key={dist} 
                      dataKey={dist} 
                      fill={distributorColors[index % distributorColors.length]}
                      name={dist}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Graph 3: Invoices by Month */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-gray-800">
              3. Valor Total de Faturas Emitidas por Mês de Referência
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invoicesByMonth.length === 0 ? (
              <p className="text-center py-8 text-gray-500">Sem dados disponíveis</p>
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={invoicesByMonth} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    label={{ value: 'R$', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => 'R$ ' + (value / 1000).toFixed(0) + 'k'}
                  />
                  <Tooltip 
                    formatter={(value) => ['R$ ' + value.toLocaleString('pt-BR', { minimumFractionDigits: 2 }), 'Valor Total']}
                  />
                  <Bar dataKey="amount" fill="#10b981" name="Valor de Faturas" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Graph 4: Invoice Count by Month */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-gray-800">
              4. Quantidade de Faturas Emitidas por Mês de Referência
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invoicesCountByMonth.length === 0 ? (
              <p className="text-center py-8 text-gray-500">Sem dados disponíveis</p>
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={invoicesCountByMonth} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    label={{ value: 'Quantidade', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => value.toLocaleString('pt-BR')}
                  />
                  <Tooltip 
                    formatter={(value) => [value.toLocaleString('pt-BR'), 'Quantidade de Faturas']}
                  />
                  <Bar dataKey="count" fill="#f59e0b" name="Quantidade de Faturas" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Graph 5: Cooperatives by Month with Status */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl text-gray-800">
              <Users className="w-5 h-5 text-blue-600" />
              5. Cooperados Cadastrados por Mês e Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cooperativesByMonth.length === 0 ? (
              <p className="text-center py-8 text-gray-500">Sem dados disponíveis</p>
            ) : (
              <ResponsiveContainer width="100%" height={450}>
                <ComposedChart data={cooperativesByMonth} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month_display"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    label={{ value: 'Quantidade', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value, name) => [value, name]}
                    labelFormatter={(label) => `Mês: ${label}`}
                  />
                  <Legend />
                  <Bar dataKey="active" stackId="a" fill="#10b981" name="Ativos" />
                  <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pendentes" />
                  <Bar dataKey="inactive" stackId="a" fill="#6b7280" name="Inativos" />
                  <Bar dataKey="suspended" stackId="a" fill="#ef4444" name="Suspensos" />
                  <Bar dataKey="excluded" stackId="a" fill="#1f2937" name="Excluídos" />
                  <Bar dataKey="login_missing" stackId="a" fill="#8b5cf6" name="Falta Login" />
                  <Line type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={3} name="Total Cadastrados" dot={{ fill: '#3b82f6', strokeWidth: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Graph 6: Invoices by Status per Month */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl text-gray-800">
              <FileText className="w-5 h-5 text-green-600" />
              6. Faturas por Mês e Status de Pagamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invoicesByStatusMonth.length === 0 ? (
              <p className="text-center py-8 text-gray-500">Sem dados disponíveis</p>
            ) : (
              <ResponsiveContainer width="100%" height={450}>
                <ComposedChart data={invoicesByStatusMonth} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month_display"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    label={{ value: 'Quantidade', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value, name) => [value, name]}
                    labelFormatter={(label) => `Mês: ${label}`}
                  />
                  <Legend />
                  <Bar dataKey="paid" stackId="a" fill="#10b981" name="Pagas" />
                  <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pendentes" />
                  <Bar dataKey="overdue" stackId="a" fill="#ef4444" name="Vencidas" />
                  <Bar dataKey="cancelled" stackId="a" fill="#6b7280" name="Canceladas" />
                  <Bar dataKey="processing" stackId="a" fill="#3b82f6" name="Processando" />
                  <Line type="monotone" dataKey="total" stroke="#8b5cf6" strokeWidth={3} name="Total Faturas" dot={{ fill: '#8b5cf6', strokeWidth: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
